import React, { useMemo } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import DotTextSkeleton from 'components/application/tableSkeletons/DotTextSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';
import TruncatableText from 'components/application/TruncatableText';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import ApprovalStatusIndicator from 'components/projects/components/ApprovalStatusIndicator';
import LifecycleStatusIndicator from 'components/projects/components/LifecycleStatusIndicator';
import ReviewStatusIndicator from 'components/projects/components/ReviewStatusIndicator';
import RiskAssessmentsStatusIndicator from 'components/projects/components/RiskAssessmentsStatusIndicator';

import { formatDate } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function ProjectsTable({
  domainDashboardReport,
  hasNextPage,
  onProjectClick,
  selections,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nTCommon } = useTranslation('common');

  const { domainDashboardConfigurableFields } =
    useProjectsAnalyticsDashboardContext();

  const buildColumnsArgs = {
    domainDashboardConfigurableFields,
    selections,
    singularRAMSName,
    translations: {
      approval: i18nTCommon('projects.approval'),
      creator: i18nTCommon('projects.creator'),
      division: i18nTCommon('shared.division'),
      folder: i18nTCommon('projects.folder'),
      live: i18nTCommon('shared.live'),
      reviewDate: i18nTCommon('projects.reviewDate'),
      riskAssessments: i18nTCommon('projects.riskAssessments'),
    },
  };

  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    [JSON.stringify(buildColumnsArgs)],
  );

  const isDataPending = !domainDashboardReport;

  const projects =
    isDataPending ?
      []
    : domainDashboardReport.data.relationships.projects.data.map(
        ({ id }) => domainDashboardReport.included.project[id],
      );

  const data = useMemo(
    () =>
      isDataPending ?
        Array(12).fill({})
      : projects.concat(hasNextPage ? [{ type: 'skeleton' }] : []),
    [hasNextPage, isDataPending, JSON.stringify(projects)],
  );

  const included = useMemo(
    () => domainDashboardReport?.included || {},
    [JSON.stringify(domainDashboardReport?.included)],
  );

  const projectFieldsIndexedByOriginalField = useMemo(() => {
    if (isDataPending) return {};

    const fields = included.field;
    if (_.isEmpty(fields)) return {};

    return projects.reduce((acc, project) => {
      acc[project.id] =
        project.relationships.dashboardConfigurableFields.data.reduce(
          (fieldMap, dashboardConfigurableField) => {
            const field = fields[dashboardConfigurableField.id];
            fieldMap[field.relationships.originalField.data.id] = field;

            return fieldMap;
          },
          {},
        );

      return acc;
    }, {});
  }, [isDataPending, JSON.stringify(included.field), JSON.stringify(projects)]);

  const handleProjectClick = (project) => {
    onProjectClick(project);

    const redirectPath = encodeURIComponent(
      project.attributes.archivedAt ?
        `/projects/?search=${project.attributes.name}&section=archive`
      : `/projects/${project.id}`,
    );

    window.location.assign(
      `/companies/${project.relationships.company.data.id}?redirect_to=${redirectPath}`,
    );
  };

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      included,
      isDataPending,
      onProjectClick: handleProjectClick,
      projectFieldsIndexedByOriginalField,
      translations: {
        deletedUser: i18nTCommon('shared.deletedUser'),
      },
    },
  });

  return (
    <div className='tw-relative tw-h-full tw-w-full tw-overflow-y-auto'>
      <table className='tw-min-w-full tw-table-fixed tw-border-separate tw-border-spacing-0 tw-bg-white'>
        <thead className='tw-sticky tw-top-0 tw-z-[1] tw-bg-grey-050'>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  className='tw-border-0 tw-border-b-1 tw-border-t-1 tw-border-solid tw-border-grey-100'
                  key={header.id}
                >
                  <div
                    className={classNames(
                      'tw-flex tw-h-10 tw-items-center tw-justify-start tw-truncate tw-px-2 tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900',
                      header.column.getIsFirstColumn() && 'tw-ml-2',
                      header.column.getIsLastColumn() && 'tw-mr-2',
                      header.column.columnDef.meta?.classNames,
                    )}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, index) => (
            <tr
              className='tw-group/row tw-bg-white hover:tw-bg-grey-025 has-[button:hover]:tw-bg-blue-025'
              key={row.id}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  className={classNames(
                    'tw-h-13 tw-cursor-pointer tw-border-0 tw-border-solid tw-border-grey-100',
                    index !== 0 && 'tw-border-t-1',
                  )}
                  key={cell.id}
                  onClick={() => {
                    table.options.meta.onProjectClick(row.original);
                  }}
                >
                  <div
                    className={classNames(
                      'tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700',
                      cell.column.getIsFirstColumn() && 'tw-ml-2',
                      cell.column.getIsLastColumn() && 'tw-mr-2',
                      cell.column.columnDef.meta?.classNames,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

ProjectsTable.propTypes = {
  domainDashboardReport: PropTypes.shape({
    data: resourceShape('dashboardReport'),
    included: PropTypes.object,
  }),
  hasNextPage: PropTypes.bool,
  onProjectClick: PropTypes.func.isRequired,
  selections: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function buildColumns({
  domainDashboardConfigurableFields,
  selections,
  singularRAMSName,
  translations,
}) {
  const dashboardConfigurableFieldIdsGroupedByLabel =
    domainDashboardConfigurableFields.data.reduce(
      (acc, dashboardConfigurableField) => {
        const { label } = dashboardConfigurableField.attributes;

        acc[label] = acc[label] || [];
        acc[label].push(dashboardConfigurableField.id);

        return acc;
      },
      {},
    );

  return [
    {
      id: 'live',
      header: translations.live,
      cell: ({ row, table }) =>
        table.options.meta.isDataPending || row.original.type === 'skeleton' ?
          <DotSkeleton />
        : <LifecycleStatusIndicator
            status={_.camelCase(row.original.meta.calculatedLifecycleStatus)}
          />,
      meta: { classNames: 'tw-justify-items-center tw-w-13' },
    },
    {
      id: 'project',
      header: voca.capitalize(singularRAMSName),
      cell: ({ row, table }) =>
        table.options.meta.isDataPending || row.original.type === 'skeleton' ?
          <TextSkeleton />
        : <TruncatableText text={row.original.attributes.name} />,
      meta: { classNames: 'tw-w-80' },
    },
    {
      id: 'division',
      header: translations.division,
      cell: ({ row, table }) => {
        if (
          table.options.meta.isDataPending ||
          row.original.type === 'skeleton'
        ) {
          return <TextSkeleton />;
        }

        const division =
          table.options.meta.included.company[
            row.original.relationships.company.data.id
          ];

        return <TruncatableText text={division.attributes.name} />;
      },
      meta: { classNames: 'tw-w-30' },
    },
    selections.includes('folder') && {
      id: 'folder',
      header: translations.folder,
      cell: ({ row, table }) => {
        if (
          table.options.meta.isDataPending ||
          row.original.type === 'skeleton'
        ) {
          return <TextSkeleton />;
        }

        const folder =
          table.options.meta.included.folder[
            row.original.relationships.folder?.data?.id
          ];

        return folder ?
            <TruncatableText text={folder.attributes.name} />
          : null;
      },
      meta: { classNames: 'tw-w-30' },
    },
    Object.entries(dashboardConfigurableFieldIdsGroupedByLabel).map(
      ([label, dashboardConfigurableFieldIds]) => ({
        id: `dashboardConfigurableField-${label}`,
        header: label,
        cell: ({ row, table }) => {
          if (
            table.options.meta.isDataPending ||
            row.original.type === 'skeleton'
          ) {
            return <TextSkeleton />;
          }

          const projectFields =
            table.options.meta.projectFieldsIndexedByOriginalField[
              row.original.id
            ];

          const text =
            _.isEmpty(projectFields) ? '' : (
              dashboardConfigurableFieldIds
                .reduce((acc, dashboardConfigurableFieldId) => {
                  const field = projectFields[dashboardConfigurableFieldId];
                  if (field) acc.push(field.attributes.value);
                  return acc;
                }, [])
                .join(', ')
            );

          return <TruncatableText text={text} />;
        },
        meta: { classNames: 'tw-w-30' },
      }),
    ),
    {
      id: 'creator',
      header: translations.creator,
      cell: ({ row, table }) => {
        if (
          table.options.meta.isDataPending ||
          row.original.type === 'skeleton'
        ) {
          return <TextSkeleton />;
        }

        const creator =
          table.options.meta.included.user[
            row.original.relationships.user?.data?.id
          ];

        return creator ?
            <TruncatableText
              text={[
                personDisplayName({ attributes: creator.attributes }),
                creator.attributes.deletedAt &&
                  table.options.meta.translations.deletedUser,
              ]
                .filter(Boolean)
                .join(' ')}
            />
          : null;
      },
      meta: { classNames: 'tw-w-30' },
    },
    selections.includes('risk_assessments') && {
      id: 'riskAssessments',
      header: translations.riskAssessments,
      cell: ({ row, table }) => {
        if (
          table.options.meta.isDataPending ||
          row.original.type === 'skeleton'
        ) {
          return <DotTextSkeleton />;
        }

        const status = _.camelCase(
          row.original.meta.calculatedRiskAssessmentsStatus,
        );

        return status && status !== 'noStatus' ?
            <RiskAssessmentsStatusIndicator status={status} />
          : null;
      },
      meta: { classNames: 'tw-w-38' },
    },
    selections.includes('review_date') && {
      id: 'reviewDate',
      header: translations.reviewDate,
      cell: ({ row, table }) => {
        if (
          table.options.meta.isDataPending ||
          row.original.type === 'skeleton'
        ) {
          return <DotTextSkeleton />;
        }

        const scheduledContentReviewRequest =
          table.options.meta.included.projectContentReviewRequest[
            row.original.relationships.scheduledContentReviewRequest?.data?.id
          ];

        const { dueDate } = scheduledContentReviewRequest?.attributes || {};

        const status = _.camelCase(row.original.meta.calculatedReviewStatus);

        return status && status !== 'noStatus' ?
            <ReviewStatusIndicator
              dueDate={
                dueDate && formatDate({ date: dueDate, format: 'dd MMM yy' })
              }
              status={status}
            />
          : null;
      },
      meta: { classNames: 'tw-w-36' },
    },
    selections.includes('approval_status') && {
      id: 'approvalStatus',
      header: translations.approval,
      cell: ({ row, table }) => {
        if (
          table.options.meta.isDataPending ||
          row.original.type === 'skeleton'
        ) {
          return <TextSkeleton />;
        }

        const status = _.camelCase(row.original.meta.approvalStatus);

        return status ?
            <div className='tw-w-fit'>
              <ApprovalStatusIndicator status={status} />
            </div>
          : null;
      },
      meta: { classNames: 'tw-w-29' },
    },
  ]
    .flat()
    .filter(Boolean);
}
